import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { Edit, Trash2, Shield, Plus, X } from 'lucide-react';
import axios from 'axios';
import { API_BASE_URL } from '../lib/constants';
import { useNotification } from '../contexts/NotificationContext';

/**
 * RoleList — displays all roles in a clean table.
 * "Add Role" button opens a popup modal to quickly create a new role by name.
 * Props:
 *   onEditRole(role) — called when the user clicks Edit on a role row
 *   onAddRole()     — called when clicking Add Role (if using standalone Add page)
 */
const RoleList = ({ onEditRole, onAddRole }) => {
    const { userRole, permissions } = useContext(AuthContext);
    const { showNotification } = useNotification();

    const [roles, setRoles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // ── Modal state ──────────────────────────────────────────────────────────
    const [showModal, setShowModal] = useState(false);
    const [newRoleName, setNewRoleName] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const inputRef = useRef(null);

    // ── Helpers ──────────────────────────────────────────────────────────────
    const hasPermission = (perm) => {
        if ((userRole || '').toLowerCase() === 'admin') return true;
        return (permissions || []).includes(perm);
    };

    // ── Fetch roles ──────────────────────────────────────────────────────────
    useEffect(() => {
        fetchRoles();
    }, []);

    // Auto-focus input when modal opens
    useEffect(() => {
        if (showModal && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 80);
        }
    }, [showModal]);

    // Close modal on Escape key
    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'Escape') closeModal();
        };
        if (showModal) window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [showModal]);

    const fetchRoles = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/get_roles`, { withCredentials: true });
            if (response.data.status === 'success') {
                setRoles(response.data.data);
            } else {
                setRoles([]);
            }
        } catch (error) {
            console.error('Error fetching roles:', error);
            setRoles([]);
        } finally {
            setIsLoading(false);
        }
    };

    // ── Modal open/close ─────────────────────────────────────────────────────
    const openModal = () => {
        setNewRoleName('');
        setShowModal(true);
    };

    const closeModal = () => {
        if (isCreating) return; // prevent close while saving
        setShowModal(false);
        setNewRoleName('');
    };

    // ── Create role (backend) ────────────────────────────────────────────────
    const handleCreateRole = async () => {
        const trimmed = newRoleName.trim();

        if (!trimmed) {
            showNotification('error', 'Role name cannot be empty.');
            inputRef.current?.focus();
            return;
        }
        if (trimmed.length < 3) {
            showNotification('error', 'Role name must be at least 3 characters.');
            inputRef.current?.focus();
            return;
        }

        setIsCreating(true);
        try {
            const payload = { name: trimmed, description: '', permissions: [] };
            const response = await axios.post(`${API_BASE_URL}/add_role`, payload, { withCredentials: true });

            showNotification('success', `Role "${trimmed}" created successfully!`);
            closeModal();
            await fetchRoles(); // refresh list
        } catch (error) {
            console.error('Error creating role:', error);
            const msg = error.response?.data?.message || 'Failed to create role. Check server.';
            showNotification('error', msg);
        } finally {
            setIsCreating(false);
        }
    };

    // Allow Enter key to submit
    const handleInputKeyDown = (e) => {
        if (e.key === 'Enter') handleCreateRole();
    };

    // ── Delete role ──────────────────────────────────────────────────────────
    const handleDelete = (role) => {
        showNotification(
            'confirm',
            `Are you sure you want to delete the role "${role.name}"? This action cannot be undone.`,
            async () => {
                try {
                    await axios.delete(`${API_BASE_URL}/delete_role/${role.role_id}`, { withCredentials: true });
                    showNotification('success', `Role "${role.name}" deleted successfully.`);
                    fetchRoles();
                } catch (err) {
                    console.error('Delete error:', err);
                    const msg = err.response?.data?.message || 'Error deleting role.';
                    showNotification('error', msg);
                }
            }
        );
    };

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <div style={{ padding: '24px', minHeight: '100vh', background: '#f8fafc' }}>

            {/* ── Header ── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: '#0f172a' }}>Role Management</h2>
                    <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#94a3b8' }}>
                        {isLoading ? 'Loading…' : `${roles.length} role${roles.length !== 1 ? 's' : ''} configured`}
                    </p>
                </div>
                {hasPermission('Roles Add') && (
                    <button
                        onClick={() => {
                            if (onAddRole) onAddRole();
                            else openModal();
                        }}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            background: '#10b981', color: '#fff', border: 'none',
                            borderRadius: '12px', padding: '11px 22px',
                            fontSize: '14px', fontWeight: 700, cursor: 'pointer',
                            boxShadow: '0 4px 14px rgba(16,185,129,0.25)',
                            transition: 'background 0.2s'
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#059669'}
                        onMouseLeave={e => e.currentTarget.style.background = '#10b981'}
                    >
                        <Plus style={{ width: 16, height: 16 }} />
                        Add Role
                    </button>
                )}
            </div>

            {/* ── Table ── */}
            <div style={{
                background: '#fff', borderRadius: '16px',
                border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                overflow: 'hidden'
            }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                            <th style={{ padding: '14px 20px', textAlign: 'center', fontSize: '11px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.1em', textTransform: 'uppercase', width: '60px' }}>#</th>
                            <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Role Name</th>
                            <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Permissions</th>
                            <th style={{ padding: '14px 20px', textAlign: 'right', fontSize: '11px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={4} style={{ padding: '48px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>
                                    Loading roles…
                                </td>
                            </tr>
                        ) : roles.length === 0 ? (
                            <tr>
                                <td colSpan={4} style={{ padding: '48px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>
                                    No roles found. Click "Add Role" to create one.
                                </td>
                            </tr>
                        ) : (
                            roles.map((role, index) => (
                                <tr
                                    key={role.role_id}
                                    style={{
                                        borderBottom: index < roles.length - 1 ? '1px solid #f1f5f9' : 'none',
                                        transition: 'background 0.15s'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#fafbfc'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                >
                                    <td style={{ padding: '18px 20px', textAlign: 'center' }}>
                                        <span style={{
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                            width: '28px', height: '28px', borderRadius: '8px',
                                            background: '#f1f5f9', color: '#64748b',
                                            fontSize: '12px', fontWeight: 700
                                        }}>
                                            {index + 1}
                                        </span>
                                    </td>
                                    <td style={{ padding: '18px 20px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <div style={{
                                                width: '36px', height: '36px', borderRadius: '10px',
                                                background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center'
                                            }}>
                                                <Shield style={{ width: 18, height: 18, color: '#10b981' }} />
                                            </div>
                                            <span style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>{role.name}</span>
                                        </div>
                                    </td>
                                    <td style={{ padding: '18px 20px' }}>
                                        <span style={{
                                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                                            background: '#eff6ff', color: '#3b82f6',
                                            borderRadius: '20px', padding: '4px 12px',
                                            fontSize: '12px', fontWeight: 700
                                        }}>
                                            {Array.isArray(role.permissions) ? role.permissions.length : 0} permissions
                                        </span>
                                    </td>
                                    <td style={{ padding: '18px 20px', textAlign: 'right' }}>
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                            {hasPermission('Roles Edit') && (
                                                <button
                                                    onClick={() => onEditRole && onEditRole(role)}
                                                    title="Edit Role"
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: '6px',
                                                        background: '#fffbeb', color: '#f59e0b',
                                                        border: '1px solid #fde68a', borderRadius: '8px',
                                                        padding: '7px 14px', fontSize: '13px', fontWeight: 700,
                                                        cursor: 'pointer', transition: 'all 0.15s'
                                                    }}
                                                    onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
                                                    onMouseLeave={e => e.currentTarget.style.background = '#fffbeb'}
                                                >
                                                    <Edit style={{ width: 14, height: 14 }} />
                                                    Edit
                                                </button>
                                            )}
                                            {hasPermission('Roles Delete') && (
                                                <button
                                                    onClick={() => handleDelete(role)}
                                                    title="Delete Role"
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: '6px',
                                                        background: '#fef2f2', color: '#ef4444',
                                                        border: '1px solid #fecaca', borderRadius: '8px',
                                                        padding: '7px 14px', fontSize: '13px', fontWeight: 700,
                                                        cursor: 'pointer', transition: 'all 0.15s'
                                                    }}
                                                    onMouseEnter={e => e.currentTarget.style.background = '#fee2e2'}
                                                    onMouseLeave={e => e.currentTarget.style.background = '#fef2f2'}
                                                >
                                                    <Trash2 style={{ width: 14, height: 14 }} />
                                                    Delete
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* ══════════════════════════════════════════════════════════════════
                ADD ROLE MODAL POPUP
            ══════════════════════════════════════════════════════════════════ */}
            {showModal && (
                <>
                    {/* Backdrop */}
                    <div
                        onClick={closeModal}
                        style={{
                            position: 'fixed', inset: 0,
                            background: 'rgba(15,23,42,0.45)',
                            backdropFilter: 'blur(3px)',
                            zIndex: 1000,
                            animation: 'fadeIn 0.15s ease'
                        }}
                    />

                    {/* Modal box */}
                    <div
                        style={{
                            position: 'fixed',
                            top: '50%', left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 1001,
                            width: '100%', maxWidth: '460px',
                            background: '#fff',
                            borderRadius: '20px',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                            overflow: 'hidden',
                            animation: 'slideUp 0.2s ease'
                        }}
                    >
                        {/* Green header */}
                        <div style={{
                            background: 'linear-gradient(135deg, #10b981, #059669)',
                            padding: '22px 24px',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{
                                    width: '36px', height: '36px', borderRadius: '10px',
                                    background: 'rgba(255,255,255,0.2)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    <Shield style={{ width: 20, height: 20, color: '#fff' }} />
                                </div>
                                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#fff' }}>
                                    Add New Role
                                </h3>
                            </div>
                            <button
                                onClick={closeModal}
                                disabled={isCreating}
                                style={{
                                    background: 'rgba(255,255,255,0.2)', border: 'none',
                                    borderRadius: '8px', width: '32px', height: '32px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    cursor: isCreating ? 'not-allowed' : 'pointer',
                                    color: '#fff', transition: 'background 0.15s'
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                            >
                                <X style={{ width: 18, height: 18 }} />
                            </button>
                        </div>

                        {/* Modal body */}
                        <div style={{ padding: '28px 24px' }}>
                            <label style={{
                                display: 'block', fontSize: '11px', fontWeight: 800,
                                color: '#64748b', letterSpacing: '0.12em',
                                textTransform: 'uppercase', marginBottom: '10px'
                            }}>
                                ROLE NAME
                            </label>
                            <input
                                ref={inputRef}
                                type="text"
                                placeholder="Enter role name (e.g. Manager)"
                                value={newRoleName}
                                onChange={e => setNewRoleName(e.target.value)}
                                onKeyDown={handleInputKeyDown}
                                disabled={isCreating}
                                style={{
                                    width: '100%', padding: '14px 16px',
                                    border: '1.5px solid #e2e8f0', borderRadius: '12px',
                                    fontSize: '15px', fontWeight: 500, color: '#0f172a',
                                    background: '#f8fafc', outline: 'none',
                                    boxSizing: 'border-box', transition: 'border-color 0.2s',
                                    opacity: isCreating ? 0.6 : 1
                                }}
                                onFocus={e => e.target.style.borderColor = '#10b981'}
                                onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                            />
                            <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#94a3b8' }}>
                                You can configure permissions after creating the role by clicking Edit.
                            </p>
                        </div>

                        {/* Divider */}
                        <div style={{ height: '1px', background: '#f1f5f9' }} />

                        {/* Footer buttons */}
                        <div style={{
                            padding: '20px 24px',
                            display: 'flex', gap: '12px'
                        }}>
                            <button
                                onClick={closeModal}
                                disabled={isCreating}
                                style={{
                                    flex: 1, padding: '14px',
                                    background: '#fff', color: '#334155',
                                    border: '1.5px solid #e2e8f0', borderRadius: '12px',
                                    fontSize: '15px', fontWeight: 700,
                                    cursor: isCreating ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.15s', opacity: isCreating ? 0.5 : 1
                                }}
                                onMouseEnter={e => { if (!isCreating) e.currentTarget.style.background = '#f8fafc'; }}
                                onMouseLeave={e => { if (!isCreating) e.currentTarget.style.background = '#fff'; }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateRole}
                                disabled={isCreating}
                                style={{
                                    flex: 1, padding: '14px',
                                    background: isCreating
                                        ? 'linear-gradient(135deg, #6ee7b7, #34d399)'
                                        : 'linear-gradient(135deg, #10b981, #059669)',
                                    color: '#fff', border: 'none', borderRadius: '12px',
                                    fontSize: '15px', fontWeight: 700,
                                    cursor: isCreating ? 'not-allowed' : 'pointer',
                                    boxShadow: isCreating ? 'none' : '0 4px 14px rgba(16,185,129,0.35)',
                                    transition: 'all 0.15s'
                                }}
                                onMouseEnter={e => { if (!isCreating) e.currentTarget.style.filter = 'brightness(1.08)'; }}
                                onMouseLeave={e => { if (!isCreating) e.currentTarget.style.filter = 'none'; }}
                            >
                                {isCreating ? 'Creating…' : 'Create Role'}
                            </button>
                        </div>
                    </div>

                    {/* Keyframe animations */}
                    <style>{`
                        @keyframes fadeIn {
                            from { opacity: 0; }
                            to   { opacity: 1; }
                        }
                        @keyframes slideUp {
                            from { opacity: 0; transform: translate(-50%, calc(-50% + 24px)); }
                            to   { opacity: 1; transform: translate(-50%, -50%); }
                        }
                    `}</style>
                </>
            )}
        </div>
    );
};

export default RoleList;
